// ============================================================================
// useCopilot — conversation state for the AM Co-pilot chat surface.
//
// Owns:
//   - the session_id (one per chat session, regenerated by startNewChat)
//   - the messages array (caller-managed; the function is stateless)
//   - sendMessage(): POST the entire conversation to am-copilot Edge Function
//   - commitProposal() / discardProposal(): per-card actions that write to
//     the underlying domain table (facility_feedback / facility_tasks) and
//     update the copilot_actions audit row.
//
// Auth: we re-use the Supabase JWT minted by the Firebase bridge — same
// token used for every RLS-gated read in the app.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase, getSupabaseToken } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';
import { addSubcontractor } from '../../subcontractors/hub/actions';
import { alreadyOnFileFrom, rememberOnFile } from '../../subcontractors/already-on-file';
import type {
  CopilotMessage,
  CopilotMessageRow,
  CopilotProposalBase,
  CopilotInvokeResponse,
} from '../../../shared/types';
import { errorMessage } from '../../../shared/errors';
import { commitWrite, refreshReaders } from '../../../shared/save-path';
import { logCount, logDelivery, pickupStock, transferStock } from '../../supply-management/dialysis-stock/api';
import type { StockResolved } from '../cards/stock-movement-card';
import { rpcErrorMessage } from '../../../shared/rpc-error';
import { readTurnStream } from '../turn-stream';
// The ask box (item #737): a drafted board action commits through the command
// center's own write — the same call its button makes.
import { writeBridge } from '../../build-room/api';
import { boardWriteBody, type BoardActionInput } from '../board-action';
import { NO_BUILDING_VALUE } from '../../tasks/no-building';
import { commitBuildingAction, type BuildingDraft } from '../building-actions';
import { commitProjectAction, type ProjectDraft } from '../project-actions';
import { commitNewAccountAction, type NewAccountDraft } from '../new-account-actions';
import { commitTaskAction, type TaskDraft } from '../task-actions';
import { commitMyWorkAction, type MyWorkDraft } from '../my-work-actions';
import { commitSubAction, type SubDraft } from '../sub-actions';
import { commitHiringAction, type HiringDraft } from '../hiring-actions';
import { commitOnboardingAction, type OnboardingDraft } from '../onboarding-actions';
import { commitSupplyAction, type SupplyDraft } from '../supply-actions';
import { commitMachineAction, type MachineDraft } from '../fleet-actions';
import { commitSeparationItem, type SeparationItemDraft } from '../separation-actions';
import { commitMileage, type MileageDraft } from '../mileage-actions';
import { commitTimeAction, type TimeFields } from '../time-actions';

/** Building tools retired by batch 3 (item 944): their drafts load from history but are not saved from here. */
export const RETIRED_BUILDING_TOOLS = [
  'propose_feedback',
  'propose_close_feedback',
  'propose_communication_log',
  'propose_facility_field_update',
  'propose_facility_access_update',
  'propose_set_rag_status',
  'propose_facility_status_change',
] as const;

/** A drafted task with no building: the choice, or nothing picked in the draft. */
export const isNoBuilding = (facilityId: unknown) =>
  facilityId == null || facilityId === '' || facilityId === NO_BUILDING_VALUE;
// ⛔ 2C — the copilot commits a project through the SAME function the entry
// screen uses. A second create path that knows a different subset of the row is
// exactly how saveProjectServiceLine started NULLing columns nobody named.
import { createProjectWithLines } from '../../project-work/hooks/use-project-data';
import {
  PROJECT_EVENT_CLASS,
  useServiceCatalog,
} from '../../project-work/hooks/use-service-catalog';

const FUNCTION_URL = `${
  import.meta.env.VITE_SUPABASE_URL as string
}/functions/v1/am-copilot`;

// 2026-05-23: persist the most recent conversation_id so an explicit
// "Resume last" affordance can reopen it. Cleared on startNewChat.
// 2026-08-07 (CW-G Phase 1, AFB-0066): this key NO LONGER auto-resumes on
// mount. /copilot opens a new, empty thread by default; resuming is an explicit
// user action. Ben's complaint was that a cold open inherited the previous
// chat's proposal cards. Keep the key for the Resume-last button only.
const LAST_CONV_KEY = 'tcs_copilot_last_conv_id';

// Hard cap on proposal cards rehydrated into one conversation. The previous
// code had no limit at all. Real conversations top out in the low tens; this
// is a guard against a pathological thread, not a product constraint.
const MAX_REHYDRATED_PROPOSALS = 200;

// A turn that has not answered in this long is given up on: the words go back
// in the box and Retry is offered (decision 7 B). Most turns take 7–12 s.
export const SEND_TIMEOUT_MS = 60_000;

/** A failed call to Ask AI in plain words — never "HTTP 500" or "Load failed". */
export function plainSendError(status: number | null, replyText?: unknown): string {
  if (typeof replyText === 'string' && replyText.trim()) return replyText.trim();
  if (status === null) return 'Ask AI could not be reached — check your signal.';
  if (status === 401) return 'Your sign-in has expired. Reload the page to sign in again.';
  if (status === 403) return 'That chat is not available any more. Start a new chat.';
  return `Ask AI could not answer (a server error, ${status}). Try again in a moment.`;
}

/** How many a committed close really closed, from what copilot_actions recorded. */
export function closeResultFrom(tool: string, payload: unknown): CopilotProposalBase['result'] {
  if (tool !== 'propose_close_feedback' && tool !== 'propose_close_task' && tool !== 'propose_building_action') return undefined;
  const p = payload as { target_row_ids?: unknown; not_closed_ids?: unknown } | null;
  if (!p || !Array.isArray(p.target_row_ids)) return undefined;
  const notClosed = Array.isArray(p.not_closed_ids) ? p.not_closed_ids.length : 0;
  return { done: p.target_row_ids.length, of: p.target_row_ids.length + notClosed };
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface CommitResult {
  ok: boolean;
  target_row_id?: string;
  error?: string;
  /** What actually happened when it differs from what was asked (a partial close). */
  note?: string;
}

export interface UseCopilotReturn {
  sessionId: string;
  conversationId: string | null;
  messages: CopilotMessage[];
  isSending: boolean;
  error: string | null;
  /** The words of a turn that failed — put back in the box, and what Retry sends. */
  failedText: string | null;
  /** The reply so far, while it is still being written (null otherwise). */
  streamingText: string | null;
  sendMessage: (text: string) => Promise<void>;
  loadConversation: (id: string, opts?: { quiet?: boolean }) => Promise<void>;
  // 2026-08-07 (CW-G Phase 1): explicit replacement for the deleted mount-time
  // auto-resume. True when a last-conversation pointer exists to offer.
  hasResumableConversation: boolean;
  resumeLast: () => Promise<void>;
  commitProposal: (
    action_id: string,
    edited_input: Record<string, unknown>,
    was_edited: boolean,
    facility_lookup: (fid: string) => string | null,
  ) => Promise<CommitResult>;
  discardProposal: (action_id: string) => Promise<void>;
  setProposalStatus: (
    action_id: string,
    status: CopilotProposalBase['status'],
    target_row_id?: string,
    result?: CopilotProposalBase['result'],
  ) => void;
  startNewChat: () => void;
}

export function useCopilot(): UseCopilotReturn {
  const { employee, profile } = useAuth();
  // The controlled vocabulary, for resolving the AI's event division to a
  // class. ⛔ Read from the catalog, never from a literal — same rule as the
  // entry screen and the helper's two doors.
  const { eventClasses } = useServiceCatalog();
  // user_profiles.id IS auth.uid() on this project (see shared/auth.tsx), and
  // copilot_actions.user_id is that same uuid. Needed explicitly because the
  // admin RLS policy does NOT scope the read for us.
  const userId = profile?.id ?? null;
  const [sessionId, setSessionId] = useState<string>(() => newSessionId());
  // 2026-05-21: conversationId is the canonical chat anchor (server-minted
  // UUID). null until the first turn is sent — the edge function creates
  // a copilot_conversations row and returns its id, which we then echo
  // back on every follow-up turn.
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedText, setFailedText] = useState<string | null>(null);
  // The reply's words while they are still arriving (null when nothing is).
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const sendingRef = useRef(false);
  // Declared up here (not next to resumeLast) so loadConversation, which is
  // defined above resumeLast, can flip it without a use-before-declare read.
  const [hasResumableConversation, setHasResumable] = useState<boolean>(false);

  const startNewChat = useCallback(() => {
    setSessionId(newSessionId());
    setConversationId(null);
    setMessages([]);
    setError(null);
    setFailedText(null);
    try {
      localStorage.removeItem(LAST_CONV_KEY);
    } catch {
      /* localStorage unavailable — ignore */
    }
    // Nothing left to resume once the pointer is cleared.
    setHasResumable(false);
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      setError(null);
      setFailedText(null);
      setIsSending(true);
      sendingRef.current = true;
      const controller = new AbortController();
      // Given up on after SEND_TIMEOUT_MS with no sign of life: each piece of
      // a streamed reply starts the wait again, so a long answer that keeps
      // arriving is never cut off, and a stalled one still is.
      let timer = window.setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);
      const stillAlive = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);
      };

      // Build full conversation for the function call
      const nextUserMsg: CopilotMessage = { role: 'user', content: trimmed };
      const optimistic: CopilotMessage[] = [...messages, nextUserMsg];
      setMessages(optimistic);

      try {
        const token = getSupabaseToken();
        if (!token) {
          throw new Error('Not authenticated — Supabase token missing.');
        }

        // Caller-managed history: send the prior turns only — the new user
        // turn travels separately in `new_user_message` so the server can
        // persist it exactly once before invoking the model. This matches
        // the 2026-05-21 persistent-chat contract on the edge function.
        const apiMessages = messages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const res = await fetch(FUNCTION_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: sessionId,
            conversation_id: conversationId,
            messages: apiMessages,
            new_user_message: trimmed,
            // Batch 2: the words arrive as they are written.
            stream: true,
            // Batch 3: this app shows the Buildings card (propose_building_action).
            building_cards: true,
            // The Projects part: this app shows the Projects card (propose_project_action).
            project_cards: true,
            // The New Accounts part: this app shows the New Accounts card (propose_new_account_action).
            new_account_cards: true,
            // The Tasks part: this app shows the task card (propose_task_action).
            task_cards: true,
            // The My Work part: this app shows the My Work card (propose_my_work_action).
            my_work_cards: true,
            // The Subcontractors part: this app shows the subcontractor card (propose_sub_action).
            sub_cards: true,
            // The hiring part: this app shows the hiring card (propose_hiring_action).
            hiring_cards: true,
            // The onboarding part: this app shows the onboarding card (propose_onboarding_action).
            onboarding_cards: true,
            // The supply part: this app shows the supply approval card (propose_supply_action).
            supply_cards: true,
            // The fleet part: this app shows the machine card (propose_machine_action).
            machine_cards: true,
            // The separations part: this app shows the separation checklist card (propose_separation_item).
            separation_cards: true,
            // "Record my mileage": this app shows the mileage card (propose_mileage).
            mileage_cards: true,
            // The Time & Labor part: this app shows the missed-shift card (propose_time_action).
            time_cards: true,
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          if (res.status === 429) {
            const mins = Math.max(1, Math.ceil(Number(errBody.retry_after_seconds ?? 60) / 60));
            throw new Error(
              `You have sent Ask AI a lot this hour. Try again in ${mins} minute${mins === 1 ? '' : 's'}.`,
            );
          }
          throw new Error(plainSendError(res.status, errBody.reply_text));
        }

        // A stream when the function sent one; the one JSON body otherwise
        // (a function deployed before streaming answers that way).
        const isStream = (res.headers.get('content-type') ?? '').includes('text/event-stream') && !!res.body;
        const data: CopilotInvokeResponse = isStream
          ? await readTurnStream(res.body!, (soFar) => setStreamingText(soFar), stillAlive)
          : ((await res.json()) as CopilotInvokeResponse);
        setStreamingText(null);

        // The server may have just minted a fresh conversation_id — capture
        // it so follow-up turns hit the same row.
        if (data.conversation_id && data.conversation_id !== conversationId) {
          setConversationId(data.conversation_id);
          try {
            localStorage.setItem(LAST_CONV_KEY, data.conversation_id);
          } catch {
            /* localStorage unavailable — ignore */
          }
        }

        // Extract assistant text from the model's final content blocks
        const textBlocks = (data.message.content || [])
          // deno-lint-ignore no-explicit-any
          .filter((b: any) => b?.type === 'text')
          // deno-lint-ignore no-explicit-any
          .map((b: any) => b.text as string);
        const assistantText = textBlocks.join('\n\n').trim();

        const assistantProposals: CopilotProposalBase[] = data.proposals.map(
          (p: CopilotInvokeResponse['proposals'][number]) => ({
            action_id: p.action_id,
            tool_name: p.tool_name,
            input: p.input,
            status: 'Proposed' as const,
            target_row_id: null,
          }),
        );

        const assistantMsg: CopilotMessage = {
          role: 'assistant',
          content:
            assistantText ||
            (assistantProposals.length > 0
              ? `I've drafted ${assistantProposals.length} action${
                  assistantProposals.length === 1 ? '' : 's'
                } for you to review.`
              : ''),
          proposals: assistantProposals,
        };

        setMessages([...optimistic, assistantMsg]);
      } catch (e) {
        // Nothing typed is lost: the words go back in the box, and the turn
        // that never got an answer is not sent again as history.
        setStreamingText(null);
        setMessages(messages);
        setFailedText(trimmed);
        const aborted = e instanceof DOMException && e.name === 'AbortError';
        const unreachable = e instanceof TypeError;
        setError(
          aborted
            ? 'Ask AI took too long to answer. Your message is back in the box — tap Retry.'
            : `${unreachable ? plainSendError(null) : errorMessage(e, plainSendError(null))} Your message is back in the box.`,
        );
      } finally {
        window.clearTimeout(timer);
        sendingRef.current = false;
        setIsSending(false);
      }
    },
    [isSending, messages, sessionId, conversationId],
  );

  // ──────────────────────────────────────────────────────────────────────
  // Load a persisted conversation from the DB into local state. The server
  // is authoritative — the loaded payload replaces any local optimistic
  // copy of `messages`. Added 2026-05-21 with the persistent-chat layer.
  //
  // Re-hydrates proposal cards from copilot_actions where the audit row
  // hangs off the same session_id. The proposal payload itself lives in
  // copilot_actions.ai_proposal; status comes from copilot_actions.status.
  // ──────────────────────────────────────────────────────────────────────
  const loadConversation = useCallback(async (id: string, opts?: { quiet?: boolean }) => {
    const quiet = !!opts?.quiet;
    if (!quiet) {
      setError(null);
      setIsSending(true);
    }
    try {
      const { data: rows, error: qErr } = await supabase
        .from('copilot_messages')
        .select('*')
        .eq('conversation_id', id)
        .order('created_at', { ascending: true });
      if (qErr) {
        if (!quiet) setError(qErr.message);
        return;
      }
      const hydrated: CopilotMessage[] = ((rows ?? []) as CopilotMessageRow[]).map(
        (r) => {
          // Persisted assistant rows may carry tool_calls (the Anthropic
          // tool_use blocks). For now we surface them via text-only — the
          // proposal cards re-hydrate from copilot_actions below, keyed by
          // action_id. The chat-display fidelity ("I drafted N actions…")
          // is preserved by the original `content` text the model emitted.
          return {
            role: r.role,
            content: r.content ?? '',
          };
        },
      );

      // Re-hydrate proposal cards for THIS conversation only.
      //
      // 2026-08-07 (CW-G Phase 1, AFB-0066). This used to fetch copilot_actions
      // with no user filter, no conversation filter and no limit, then filter
      // client-side by the chat's timestamp window and staple every match onto
      // the last assistant turn. Two bugs came out of that:
      //   1. `copilot_actions_select_admin` grants SELECT on ALL rows to
      //      has_full_am_authority(), so for admins the unfiltered fetch pulled
      //      OTHER users' proposals into their chat — a cross-user display leak,
      //      not merely noise. The `user_id` filter below closes it. RLS is
      //      deliberately left as-is: admins are supposed to be able to READ the
      //      audit trail; they were never supposed to see it rendered as their
      //      own live proposal cards. This was a query bug, not an RLS bug.
      //   2. Overlapping conversations meant one chat's proposals appeared in
      //      another. `conversation_id` (added in the same PR) is now the anchor.
      // The timestamp-window filter is gone entirely — its own comment conceded
      // it was "best-effort visual rehydration".
      //
      // Historic rows whose conversation the backfill could not determine
      // unambiguously carry conversation_id = NULL and simply do not rehydrate.
      // They remain intact as audit records.
      if (hydrated.length > 0 && userId) {
        const { data: actionRows, error: aErr } = await supabase
          .from('copilot_actions')
          .select(
            'action_id, tool_name, ai_proposal, status, target_row_id, created_at, session_id, committed_payload',
          )
          .eq('user_id', userId)
          .eq('conversation_id', id)
          .order('created_at', { ascending: true })
          .limit(MAX_REHYDRATED_PROPOSALS);
        if (aErr) {
          // Non-fatal: the transcript still renders, just without cards.
          console.warn('[copilot] proposal rehydration failed', aErr);
        }

        const proposals: CopilotProposalBase[] = (actionRows ?? []).map((a) => ({
          action_id: a.action_id as string,
          tool_name: a.tool_name as CopilotProposalBase['tool_name'],
          input: (a.ai_proposal as Record<string, unknown>) ?? {},
          status: a.status as CopilotProposalBase['status'],
          target_row_id: (a.target_row_id as string | null) ?? null,
          result: closeResultFrom(a.tool_name as string, a.committed_payload),
        }));

        if (proposals.length > 0) {
          // Attach to the most recent assistant turn (typical model output
          // pattern: assistant message follows the proposals it created).
          for (let i = hydrated.length - 1; i >= 0; i--) {
            if (hydrated[i].role === 'assistant') {
              hydrated[i] = { ...hydrated[i], proposals };
              break;
            }
          }
        }
      }

      setMessages(hydrated);
      setConversationId(id);
      try {
        localStorage.setItem(LAST_CONV_KEY, id);
        setHasResumable(true);
      } catch {
        /* localStorage unavailable — ignore */
      }
    } finally {
      if (!quiet) setIsSending(false);
    }
    // userId is a real dependency now: the rehydration query filters on it.
  }, [userId]);

  // ──────────────────────────────────────────────────────────────────────
  // 2026-08-07 (CW-G Phase 1, AFB-0066) — /copilot NO LONGER auto-resumes.
  //
  // The mount effect that used to call loadConversation(LAST_CONV_KEY) has
  // been deleted. Opening /copilot cold now gives an empty new thread with no
  // inherited proposal cards, which is what Ben asked for. Prior threads are
  // still one click away in the conversation rail, and the most recent one is
  // one click away via `resumeLast` below. Nothing is hidden — we just stop
  // defaulting into it.
  //
  // `hasResumableConversation` is read once at mount so the button can render
  // without a DB round-trip; the id itself is re-read at click time.
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      setHasResumable(Boolean(localStorage.getItem(LAST_CONV_KEY)));
    } catch {
      setHasResumable(false);
    }
  }, []);

  // Coming back to the app (phone locked, another app opened): reload the open
  // chat so a reply that finished while it was in the background appears.
  const hiddenAtRef = useRef<number | null>(null);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const away = hiddenAtRef.current === null ? 0 : Date.now() - hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (away < 5_000 || !conversationId || sendingRef.current) return;
      void loadConversation(conversationId, { quiet: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [conversationId, loadConversation]);

  const resumeLast = useCallback(async () => {
    let lastId: string | null = null;
    try {
      lastId = localStorage.getItem(LAST_CONV_KEY);
    } catch {
      lastId = null;
    }
    if (!lastId) return;
    try {
      await loadConversation(lastId);
    } catch {
      // The conversation was deleted or archived out from under us — drop the
      // pointer so the button stops offering a dead thread.
      try {
        localStorage.removeItem(LAST_CONV_KEY);
      } catch {
        /* ignore */
      }
      setHasResumable(false);
    }
  }, [loadConversation]);

  // ──────────────────────────────────────────────────────────────────────
  // Update local proposal status (called by cards on commit/discard).
  // ──────────────────────────────────────────────────────────────────────
  const setProposalStatus = useCallback(
    (
      action_id: string,
      status: CopilotProposalBase['status'],
      target_row_id?: string,
      result?: CopilotProposalBase['result'],
    ) => {
      setMessages((prev) =>
        prev.map((m: CopilotMessage) => {
          if (m.role !== 'assistant' || !m.proposals) return m;
          return {
            ...m,
            proposals: m.proposals.map((p: CopilotProposalBase) =>
              p.action_id === action_id
                ? { ...p, status, target_row_id: target_row_id ?? p.target_row_id, result: result ?? p.result }
                : p,
            ),
          };
        }),
      );
    },
    [],
  );

  // ──────────────────────────────────────────────────────────────────────
  // commitProposal — runs the actual insert into the target table, then
  // updates copilot_actions. Caller supplies the edited input + a function
  // that maps facility_id → account_id.
  // ──────────────────────────────────────────────────────────────────────
  const commitProposal = useCallback(
    async (
      action_id: string,
      edited_input: Record<string, unknown>,
      was_edited: boolean,
      facility_lookup: (fid: string) => string | null,
    ): Promise<CommitResult> => {
      // Find the proposal so we know its tool_name and original input
      let originalToolName: CopilotProposalBase['tool_name'] | null = null;
      let originalInput: Record<string, unknown> | null = null;
      for (const m of messages) {
        for (const p of m.proposals ?? []) {
          if (p.action_id === action_id) {
            originalToolName = p.tool_name;
            originalInput = p.input;
          }
        }
      }
      if (!originalToolName) {
        return { ok: false, error: 'proposal not found' };
      }

      // Board action (item #737; TCSD-0416) ───────────────────────────────
      // No facility and no table write here: the command center's bridge does
      // the write, as the button does, and refuses what the button would refuse.
      if (originalToolName === 'propose_board_action') {
        const body = boardWriteBody(edited_input as unknown as BoardActionInput);
        const r = await writeBridge(body as Parameters<typeof writeBridge>[0]);
        if (!r.ok) return { ok: false, error: r.message };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { ...body, wrote: r.data.wrote, signed_by: r.data.signed_by },
            target_table: 'build_board',
            target_row_id: String(body.item),
            committed_at: new Date().toISOString(),
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, r.data.wrote);
        // The command center, if it is open, re-reads the board.
        refreshReaders(['build_board']);
        return { ok: true, target_row_id: r.data.wrote };
      }

      // The Buildings part (batch 3, item 944) ─────────────────────────────
      // Every building action saves through the building screen's own function
      // (building-actions.ts → commitBuildingAction). copilot_actions records
      // Committed only when the save landed, with what was written.
      if (originalToolName === 'propose_building_action') {
        const draft = edited_input as unknown as BuildingDraft;
        const res = await commitBuildingAction(draft, draft.fields ?? {}, employee?.employee_id ?? null);
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited && originalInput
              ? `changed: ${Object.keys(draft.fields ?? {}).filter((k) => JSON.stringify((originalInput!.fields as Record<string, unknown> | undefined)?.[k] ?? null) !== JSON.stringify(draft.fields?.[k] ?? null)).join(', ')}`
              : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined, res.result);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The Projects part (item 944) ─────────────────────────────────────
      // Every project action saves through the project screen's own function
      // (project-actions.ts → commitProjectAction).
      if (originalToolName === 'propose_project_action') {
        const draft = edited_input as unknown as ProjectDraft;
        const res = await commitProjectAction(draft, draft.fields ?? {}, employee?.employee_id ?? null);
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, project_id: draft.project_id, ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // "Record my mileage" (item 1011) ────────────────────────────────
      // Saved through the driver tile's own submitOdometer (mileage-actions.ts).
      if (originalToolName === 'propose_mileage') {
        const draft = edited_input as unknown as MileageDraft;
        const res = await commitMileage(draft, draft.odometer, employee?.employee_id ?? null);
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { vehicle_id: draft.vehicle_id, odometer: draft.odometer },
            target_table: 'vehicle_mileage_readings',
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The separations part (item 944) ────────────────────────────────
      // Tick or hand on one checklist item, through use-separation-data.ts (separation-actions.ts).
      if (originalToolName === 'propose_separation_item') {
        const draft = edited_input as unknown as SeparationItemDraft;
        const res = await commitSeparationItem(draft, draft.fields ?? {});
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, item_id: draft.item_id, ...(res.committed_payload ?? {}) },
            target_table: 'separation_checklist_items',
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The fleet part (item 944) ──────────────────────────────────────
      // A step on one floor machine, saved through use-asset-location.ts (fleet-actions.ts).
      if (originalToolName === 'propose_machine_action') {
        const draft = edited_input as unknown as MachineDraft;
        const res = await commitMachineAction(draft, draft.fields ?? {});
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, machine_id: draft.machine_id, ...(res.committed_payload ?? {}) },
            target_table: 'floor_machines',
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The supply part (item 944) ─────────────────────────────────────
      // Approve or reject one supply request, saved through approvals.ts (supply-actions.ts).
      if (originalToolName === 'propose_supply_action') {
        const draft = edited_input as unknown as SupplyDraft;
        const res = await commitSupplyAction(draft, draft.fields ?? {});
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, request_id: draft.request_id, ...(res.committed_payload ?? {}) },
            target_table: 'supply_requests',
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The onboarding part (item 944) ─────────────────────────────────
      // A step on one onboarding card, saved through patchOnboarding (onboarding-actions.ts).
      if (originalToolName === 'propose_onboarding_action') {
        const draft = edited_input as unknown as OnboardingDraft;
        const res = await commitOnboardingAction(draft, draft.fields ?? {});
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, onboarding_id: draft.onboarding_id, ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The hiring part (item 944) ─────────────────────────────────────
      // A step on one hiring request, saved through use-hiring-data.ts (hiring-actions.ts).
      if (originalToolName === 'propose_hiring_action') {
        const draft = edited_input as unknown as HiringDraft;
        const res = await commitHiringAction(draft, draft.fields ?? {});
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, request_id: draft.request_id, ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The Subcontractors part (item 944) ───────────────────────────────
      // A step on one sub, saved through hub/actions.ts (sub-actions.ts).
      if (originalToolName === 'propose_sub_action') {
        const draft = edited_input as unknown as SubDraft;
        const res = await commitSubAction(draft, draft.fields ?? {}, employee?.employee_id ?? null);
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, provider_id: draft.provider_id, ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The My Work part (item 944) ───────────────────────────────────────
      // A row's own buttons, saved through hand-off.ts (my-work-actions.ts).
      if (originalToolName === 'propose_my_work_action') {
        const draft = edited_input as unknown as MyWorkDraft;
        const res = await commitMyWorkAction(draft, draft.fields ?? {}, employee?.employee_id ?? null);
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, item_type: draft.item_type, item_id: draft.item_id, ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The Tasks part (item 944) ─────────────────────────────────────────
      // Every task step saves through the task screen's own function
      // (task-actions.ts → commitTaskAction).
      // The Time & Labor part (item #982) ───────────────────────────────────
      // A missed shift saves through Timecards' own function
      // (time-actions.ts → commitTimeAction → manualSegment).
      if (originalToolName === 'propose_time_action') {
        const fields = (edited_input as { fields?: TimeFields }).fields;
        if (!fields) return { ok: false, error: 'The card sent nothing to save.' };
        const res = await commitTimeAction(fields);
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: 'record_shift', ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      if (originalToolName === 'propose_task_action') {
        const draft = edited_input as unknown as TaskDraft;
        const res = await commitTaskAction(draft, draft.fields ?? {}, employee?.employee_id ?? null);
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, task_id: draft.task_id, ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // The New Accounts part (item 944) ─────────────────────────────────
      // Every new-account action saves through the card screen's own function
      // (new-account-actions.ts → commitNewAccountAction).
      if (originalToolName === 'propose_new_account_action') {
        const draft = edited_input as unknown as NewAccountDraft;
        const res = await commitNewAccountAction(draft, draft.fields ?? {}, employee?.employee_id ?? null);
        if (!res.ok) return { ok: false, error: res.error };
        const status = was_edited ? 'Edited_And_Committed' : 'Committed';
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status,
            committed_payload: { action: draft.action, sop_id: draft.sop_id, ...(res.committed_payload ?? {}) },
            target_table: res.target_table ?? null,
            target_row_id: res.target_row_id ?? null,
            committed_at: new Date().toISOString(),
            user_edit_summary: was_edited ? 'edited on the card' : null,
          })
          .eq('action_id', action_id);
        if (auditErr) console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        setProposalStatus(action_id, status, res.target_row_id ?? undefined);
        return { ok: true, target_row_id: res.target_row_id ?? undefined, note: res.note };
      }

      // A draft from a retired building tool (batch 3) cannot be saved here.
      if ((RETIRED_BUILDING_TOOLS as readonly string[]).includes(originalToolName)) {
        return { ok: false, error: 'This draft was made by an earlier Ask AI. Ask again, or do it on the building.' };
      }

      const facility_id = edited_input.facility_id as string;

      // ──────────────────────────────────────────────────────────────────
      // Close-action branch — batch UPDATE (no INSERT). RLS enforces that
      // the AM owns each row; partial-permission failures surface inline.
      // account_id lookup is not required for UPDATE — we never touch it.
      // ──────────────────────────────────────────────────────────────────
      if (originalToolName === 'propose_close_task') {
        // (Feedback closes are the building tool's close_feedback since batch 3.)
        const closeTable = 'facility_tasks';
        const closeIdCol = 'task_id';
        const ids = (edited_input.task_ids as string[] | undefined) ?? [];
        if (ids.length === 0) {
          return { ok: false, error: 'no rows selected to close' };
        }
        const todayDate = new Date().toISOString().split('T')[0];
        const closePatch: Record<string, unknown> = {
          status: 'Done',
          completion_notes: ((edited_input.completion_notes as string) ?? '').trim(),
          completed_by: employee?.employee_id ?? null,
          completed_date: todayDate,
        };

        // ⛔ A PostgREST UPDATE refused by RLS returns 0 rows and NO error
        // (PLAYBOOK §2). So read back what was written and compare: the card
        // says exactly how many closed, and copilot_actions records Committed
        // only when something did.
        const { data: updated, error: updErr } = await supabase
          .from(closeTable)
          .update(closePatch)
          .eq('facility_id', facility_id)
          .in(closeIdCol, ids)
          .select(closeIdCol);
        if (updErr) {
          return { ok: false, error: rpcErrorMessage(updErr, 'Could not close these.') };
        }
        const updatedIds = (updated ?? []).map(
          (r) => (r as Record<string, string>)[closeIdCol] as string,
        );
        const notClosed = ids.filter((id) => !updatedIds.includes(id));
        if (updatedIds.length === 0) {
          return {
            ok: false,
            error: `None of the ${ids.length} ${ids.length === 1 ? 'was' : 'were'} closed — the database did not let this sign-in close ${ids.length === 1 ? 'it' : 'them'}. The building's account manager can.`,
          };
        }

        // Diff for audit summary
        let close_user_edit_summary: string | null = null;
        if (was_edited && originalInput) {
          const changed: string[] = [];
          for (const k of Object.keys(edited_input)) {
            if (k === 'rows') continue; // rows are read-only payload
            const a = JSON.stringify(originalInput[k] ?? null);
            const b = JSON.stringify(edited_input[k] ?? null);
            if (a !== b) changed.push(k);
          }
          if (changed.length > 0) {
            close_user_edit_summary = `changed: ${changed.join(', ')}`;
          }
        }

        const committed_payload: Record<string, unknown> = {
          facility_id,
          target_row_ids: updatedIds,
          // Asked for but not closed: the database refused them (0 rows, no error).
          not_closed_ids: notClosed,
          patch: closePatch,
        };

        // For batch ops we keep target_row_id NULL and surface the array via
        // committed_payload.target_row_ids. The per-card view link uses the
        // facility detail page, so we synthesize a "first id" as the result
        // so the toast can still show a meaningful Saved-as label.
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status: was_edited ? 'Edited_And_Committed' : 'Committed',
            committed_payload,
            target_table: closeTable,
            target_row_id: null,
            committed_at: new Date().toISOString(),
            user_edit_summary: close_user_edit_summary,
          })
          .eq('action_id', action_id);
        if (auditErr) {
          console.warn(
            '[use-copilot] failed to update copilot_actions (close)',
            auditErr,
          );
        }

        setProposalStatus(
          action_id,
          was_edited ? 'Edited_And_Committed' : 'Committed',
          updatedIds[0],
          { done: updatedIds.length, of: ids.length },
        );
        return {
          ok: true,
          target_row_id: updatedIds[0],
          note:
            notClosed.length > 0
              ? `Closed ${updatedIds.length} of ${ids.length}. ${notClosed.length} ${notClosed.length === 1 ? 'was' : 'were'} not closed — the database did not let this sign-in close ${notClosed.length === 1 ? 'it' : 'them'}.`
              : undefined,
        };
      }

      // ──────────────────────────────────────────────────────────────────
      // v2 expansion (2026-05-21) — 8 new commit branches.
      // Each computes its own diff summary and patches copilot_actions.
      // ──────────────────────────────────────────────────────────────────

      const computeEditDiff = (
        edited: Record<string, unknown>,
        original: Record<string, unknown> | null,
      ): string | null => {
        if (!was_edited || !original) return null;
        const changed: string[] = [];
        for (const k of Object.keys(edited)) {
          const a = JSON.stringify(original[k] ?? null);
          const b = JSON.stringify(edited[k] ?? null);
          if (a !== b) changed.push(k);
        }
        return changed.length > 0 ? `changed: ${changed.join(', ')}` : null;
      };

      const patchAudit = async (params: {
        target_table: string;
        target_row_id: string | null;
        committed_payload: Record<string, unknown>;
      }) => {
        const edit_summary = computeEditDiff(edited_input, originalInput);
        const { error: auditErr } = await supabase
          .from('copilot_actions')
          .update({
            status: was_edited ? 'Edited_And_Committed' : 'Committed',
            committed_payload: params.committed_payload,
            target_table: params.target_table,
            target_row_id: params.target_row_id,
            committed_at: new Date().toISOString(),
            user_edit_summary: edit_summary,
          })
          .eq('action_id', action_id);
        if (auditErr) {
          console.warn('[use-copilot] failed to update copilot_actions', auditErr);
        }
      };

      // Stock movement (copilot-stock-in-one-sentence, 2026-10-02) ─────────
      // Facility-less: the server resolved the clinic and locations into
      // input.resolved. Commits through the existing stock writes as the
      // signed-in user (TCSD-0321); the only edit is which location.
      if (originalToolName === 'propose_stock_movement') {
        const r = (edited_input.resolved ?? originalInput?.resolved) as StockResolved | undefined;
        if (!r) return { ok: false, error: 'nothing was worked out for this card — ask again' };
        const office = (edited_input.selected_office_id as string | null) || r.selected_office_id || '';
        try {
          let rows: unknown[] = [];
          if (r.kind === 'delivery') {
            rows = await logDelivery({
              facilityId: r.clinic?.facility_id ?? '',
              officeId: office,
              counts: [],
              leaves: [{ supply_id: r.supply_id, qty: r.qty }],
              occurredAt: r.occurred_at,
              notes: r.notes,
            });
          } else if (r.kind === 'pickup') {
            rows = await pickupStock({
              facilityId: r.clinic?.facility_id ?? '',
              officeId: office,
              supplyId: r.supply_id,
              qty: r.qty,
              occurredAt: r.occurred_at,
              notes: r.notes,
            });
          } else if (r.kind === 'transfer') {
            rows = await transferStock({
              from: r.from?.office_id ?? '',
              to: r.to?.office_id ?? '',
              supplyId: r.supply_id,
              qty: r.qty,
              occurredAt: r.occurred_at,
              notes: r.notes,
            });
          } else {
            rows = await logCount({
              facilityId: r.at?.facility_id ?? null,
              officeId: r.at?.office_id ?? null,
              counts: [{ supply_id: r.supply_id, qty: r.after ?? r.qty }],
              source: 'self',
              occurredAt: r.occurred_at,
              notes: r.notes,
            });
          }
          const ids = (rows as Array<{ movement_id?: string | null }>).map((x) => x.movement_id).filter((x): x is string => !!x);
          await patchAudit({
            target_table: 'stock_movements',
            target_row_id: ids[0] ?? null,
            committed_payload: { kind: r.kind, office_id: office || null, movement_ids: ids },
          });
          setProposalStatus(action_id, was_edited ? 'Edited_And_Committed' : 'Committed', ids[0]);
          return { ok: true, target_row_id: ids[0] };
        } catch (e) {
          return { ok: false, error: rpcErrorMessage(e, errorMessage(e, String(e))) };
        }
      }

      // Add subcontractor — the one door (item #683, prototype screen 9).
      // Lead (or Interviewing when they said they have met); never Bench for
      // someone who has not applied. The card then offers to send a Lead their
      // own application link — text app, or copy name / phone / link (item 869).
      // No later follow-up is promised.
      if (originalToolName === 'propose_add_subcontractor') {
        const subName = ((edited_input.name as string) ?? '').trim();
        const subPhone = ((edited_input.phone as string) ?? '').trim();
        const subMarkets = Array.isArray(edited_input.coverage_markets) ? (edited_input.coverage_markets as string[]) : [];
        const stage = edited_input.stage === 'Meet' ? 'Meet' : 'Lead';
        if (subName.length < 2) return { ok: false, error: 'sub name is required' };
        try {
          const res = await addSubcontractor({
            name: subName,
            phone: subPhone,
            markets: subMarkets,
            kind: edited_input.kind === 'both' ? 'both' : edited_input.kind === 'projects' ? 'projects' : 'nightly',
            status: stage,
            addAnyway: edited_input.add_anyway === true,
          });
          rememberOnFile(action_id, null);
          await patchAudit({
            target_table: 'service_providers',
            target_row_id: res.provider_id,
            committed_payload: { provider_id: res.provider_id, prospect_id: res.prospect_id, input: edited_input },
          });
          setProposalStatus(action_id, was_edited ? 'Edited_And_Committed' : 'Committed', res.provider_id);
          return { ok: true, target_row_id: res.provider_id };
        } catch (e) {
          // "Already on file?" shows on the card itself, with its two choices.
          const found = alreadyOnFileFrom(e);
          if (found) rememberOnFile(action_id, found);
          return { ok: false, error: errorMessage(e, String(e)) };
        }
      }

      // Hiring requests ──────────────────────────────────────────────────
      if (originalToolName === 'propose_hiring_request') {
        const accountId = facility_lookup(facility_id);
        if (!accountId) return { ok: false, error: `no account for ${facility_id}` };
        const originalMessage = (() => {
          for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'user') return messages[i].content;
          }
          return null;
        })();
        const urgencyLabelMap: Record<string, string> = {
          Emergency: 'High',
          Soon: 'Medium',
          Planned: 'Low',
        };
        const urgencyValue = edited_input.urgency as string;
        const payload: Record<string, unknown> = {
          facility_id,
          account_id: accountId,
          requested_by: employee?.employee_id ?? null,
          title: ((edited_input.title as string) ?? '').trim(),
          role_type: edited_input.role_type,
          shift: ((edited_input.shift as string) ?? '').trim(),
          employment_type: edited_input.employment_type,
          urgency: urgencyValue,
          urgency_label: urgencyLabelMap[urgencyValue] ?? 'Medium',
          target_start_date: (edited_input.target_start_date as string | null) || null,
          headcount: Number(edited_input.headcount) || 1,
          // D4: no rate from Ask AI — HR sets it on the Hiring pipeline.
          notes: (edited_input.notes as string | null) || null,
          status: 'Open',
          source_channel: 'Voice_AI',
          // TCSD-0162 — owned by the HR POSITION (column default). ⛔ Never a person.
          original_message: originalMessage,
        };
        // A headcount above 1 comes back as ONE row, the first seat: the
        // database opens one request per person (item 621), the same split the
        // Hiring form gets, so the voice path needs nothing of its own.
        let trid: string;
        try {
          const [ins] = await commitWrite<{ request_id: string }>({
            label: 'open the hiring request',
            write: supabase.from('hiring_requests').insert(payload).select('request_id'),
            touches: ['hiring_requests'],
          });
          trid = ins.request_id;
        } catch (e) {
          return { ok: false, error: errorMessage(e, 'insert failed') };
        }
        await patchAudit({ target_table: 'hiring_requests', target_row_id: trid, committed_payload: payload });
        setProposalStatus(action_id, was_edited ? 'Edited_And_Committed' : 'Committed', trid);
        return { ok: true, target_row_id: trid };
      }

      // Sub replacement ─────────────────────────────────────────────────
      if (originalToolName === 'propose_sub_replacement') {
        const accountId = facility_lookup(facility_id);
        if (!accountId) return { ok: false, error: `no account for ${facility_id}` };
        const newSrrId = `SRR-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
        const payload: Record<string, unknown> = {
          request_id: newSrrId,
          facility_id,
          account_id: accountId,
          outgoing_assignment_id: edited_input.outgoing_assignment_id,
          reason: ((edited_input.reason as string) ?? '').trim(),
          // Why, from the list (#667); the card will not commit without one.
          reason_code: (edited_input.reason_code as string | null) || null,
          urgency: edited_input.urgency,
          hold_pay: !!edited_input.hold_pay,
          notes: (edited_input.notes as string | null) || null,
          requested_by_eid: employee?.employee_id ?? null,
          created_by_eid: employee?.employee_id ?? null,
          status: 'Pending',
        };
        const { data: ins, error: insErr } = await supabase
          .from('sub_replacement_requests')
          .insert(payload)
          .select('request_id')
          .single();
        if (insErr || !ins) return { ok: false, error: insErr?.message ?? 'insert failed' };
        const trid = (ins as Record<string, string>).request_id;
        await patchAudit({ target_table: 'sub_replacement_requests', target_row_id: trid, committed_payload: payload });
        setProposalStatus(action_id, was_edited ? 'Edited_And_Committed' : 'Committed', trid);
        return { ok: true, target_row_id: trid };
      }

      // Supply par update (UPSERT) ──────────────────────────────────────
      if (originalToolName === 'propose_supply_par_update') {
        const supplyId = edited_input.supply_id as string;
        const par = (edited_input.par_level as number | null) ?? null;
        const reorder = (edited_input.reorder_threshold as number | null) ?? null;
        const notesValue = (edited_input.notes as string | null) || null;
        const { data: existing } = await supabase
          .from('facility_supply_allocation')
          .select('allocation_id')
          .eq('facility_id', facility_id)
          .eq('supply_id', supplyId)
          .maybeSingle();
        const patch: Record<string, unknown> = {};
        if (par != null) patch.par_level = par;
        if (reorder != null) patch.reorder_threshold = reorder;
        if (notesValue != null) patch.notes = notesValue;
        let trid: string;
        if (existing) {
          const { data: upd, error: updErr } = await supabase
            .from('facility_supply_allocation')
            .update(patch)
            .eq('allocation_id', existing.allocation_id)
            .select('allocation_id')
            .single();
          if (updErr || !upd) return { ok: false, error: updErr?.message ?? 'update failed' };
          trid = upd.allocation_id as string;
        } else {
          // Generate a fresh allocation_id since the column has no default.
          const newAllocationId = `ALC-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
          const { data: ins, error: insErr } = await supabase
            .from('facility_supply_allocation')
            .insert({
              allocation_id: newAllocationId,
              facility_id,
              supply_id: supplyId,
              managed_by: 'AM_Copilot',
              ...patch,
            })
            .select('allocation_id')
            .single();
          if (insErr || !ins) return { ok: false, error: insErr?.message ?? 'insert failed' };
          trid = ins.allocation_id as string;
        }
        await patchAudit({
          target_table: 'facility_supply_allocation',
          target_row_id: trid,
          committed_payload: { facility_id, supply_id: supplyId, patch },
        });
        setProposalStatus(action_id, was_edited ? 'Edited_And_Committed' : 'Committed', trid);
        return { ok: true, target_row_id: trid };
      }

      // Project work ────────────────────────────────────────────────────
      // Inserts a Draft project into public.projects. project_id and margin
      // are assigned by DB triggers (trg_projects_assign_id /
      // trg_projects_compute_margin), so we never send them — mirrors
      // project-work/hooks/use-project-data.ts::createProject.
      if (originalToolName === 'propose_project_work') {
        // ⛔ THROUGH THE ONE CREATE PATH. 2026-09-03, part 2C. This used to be a
        // hand-rolled INSERT into `projects` followed by a separate INSERT into
        // `project_services`, each knowing its own subset of the row — the
        // second create path that Run C's NULLing bug was made of. It also meant
        // this screen never learned what a project became: no classification, no
        // sub, and a service line whose rates were copied off the project row.
        //
        // `createProjectWithLines` is what the entry screen uses. One function
        // knows what a project is, and the DB mints the id (TCSD-0142).
        const accountId = facility_lookup(facility_id);
        if (!accountId) return { ok: false, error: `no account for ${facility_id}` };
        if (!employee?.employee_id) return { ok: false, error: 'no employee identity for this session' };

        const serviceCode = ((edited_input.service_code as string) ?? '').trim();
        if (!serviceCode) return { ok: false, error: 'a service is required before this can be saved' };

        // ⛔ The class is resolved from the EVENT division, never from the
        // service division — two vocabularies, one column name in the database.
        // A missing division means ordinary project work, which is the default.
        const eventDivision = (edited_input.event_division_id as string | null) ?? null;
        const eventClass = eventDivision
          ? (eventClasses.find((c) => c.division_id === eventDivision)?.event_class ??
             PROJECT_EVENT_CLASS)
          : PROJECT_EVENT_CLASS;
        const reasonCode = eventDivision ? ((edited_input.reason_code as string | null) ?? null) : null;
        const notes = ((edited_input.notes as string | null) ?? '').trim() || null;

        // ⛔ NULL IS NOT ZERO (TCSD-0062). The card sends null for an amount
        // nobody stated, and the advance gate reads the difference: a NULL bill
        // blocks the send, a zero one does not.
        const bill = (edited_input.bill_amount as number | null) ?? null;
        const pay = (edited_input.pay_amount as number | null) ?? null;
        const serviceDate = (edited_input.service_date as string | null) || null;

        try {
          const created = await createProjectWithLines(
            {
              facility_id,
              account_id: accountId,
              event_class: eventClass,
              reason_code: reasonCode,
              // `projects_reason_note_required_off_project` refuses an
              // off-project row with no words. The edge function's validator
              // already refuses the proposal, so this is the second net.
              reason_note: eventDivision ? notes : null,
              start_date: serviceDate,
              completion_date: serviceDate,
              due_date: null,
              bill_amount: bill,
              pay_amount: pay,
              special_pay: false,
              awaiting_schedule: false,
              notes,
            },
            [
              {
                line_role: 'service',
                service_code: serviceCode,
                scope_text: ((edited_input.scope_text as string | null) ?? '').trim() || null,
                scope_qty: (edited_input.scope_qty as number | null) ?? null,
                client_rate: bill,
                sub_rate: pay,
                assigned_partner_id: (edited_input.assigned_partner_id as string | null) || null,
                service_date_planned: null,
                service_date_actual: serviceDate,
                line_order: 1,
              },
            ],
            employee.employee_id,
          );
          const trid = created.project_id;
          await patchAudit({
            target_table: 'projects',
            target_row_id: trid,
            committed_payload: { ...edited_input, event_class: eventClass, project_id: trid },
          });
          setProposalStatus(action_id, was_edited ? 'Edited_And_Committed' : 'Committed', trid);
          return { ok: true, target_row_id: trid };
        } catch (e) {
          return { ok: false, error: rpcErrorMessage(e, 'Could not save this project.') };
        }
      }

      // ──────────────────────────────────────────────────────────────────
      // Insert path (propose_task / propose_supply_order)
      // ──────────────────────────────────────────────────────────────────
      // A task can be "Not about one building" (TCSD-0450): no building, no
      // account, and it goes to the person who made it, as the New task button
      // does, so it lands on somebody's My Work.
      const noBuilding = originalToolName === 'propose_task' && isNoBuilding(facility_id);
      const account_id = noBuilding ? null : facility_lookup(facility_id);
      if (!noBuilding && !account_id) {
        return { ok: false, error: `no account for ${facility_id}` };
      }

      let target_table: 'facility_tasks' | 'supply_requests';
      let payload: Record<string, unknown>;

      if (originalToolName === 'propose_task') {
        target_table = 'facility_tasks';
        payload = {
          facility_id: noBuilding ? null : facility_id,
          account_id,
          ...(noBuilding ? { assigned_to: employee?.employee_id ?? null } : {}),
          description: (edited_input.description as string)?.trim(),
          priority: edited_input.priority,
          status: 'Open',
          due_date: (edited_input.due_date as string | null) ?? null,
          source: 'AM_Copilot',
          created_by: employee?.employee_id ?? null,
        };
      } else {
        // propose_supply_order — insert into supply_requests.
        // Mirrors src/modules/supply-management/new-request.tsx payload.
        target_table = 'supply_requests';
        const supply_id = (edited_input.supply_id as string | null) ?? null;
        const item_description =
          ((edited_input.item_description as string | null) ?? '').trim();
        payload = {
          facility_id,
          account_id,
          supply_id,
          item_description: item_description || null,
          quantity: Number(edited_input.quantity),
          unit_of_measure:
            ((edited_input.unit_of_measure as string | null) ?? '')
              .trim()
              .toUpperCase() || null,
          urgency: edited_input.urgency,
          date_needed: (edited_input.date_needed as string | null) || null,
          requested_by: employee?.employee_id ?? null,
          ship_to_address: null,
          reimbursable: false,
          notes: ((edited_input.notes as string | null) ?? '').trim() || null,
          status: 'Submitted',
        };
      }

      // Insert into the target table
      const idCol = target_table === 'facility_tasks' ? 'task_id' : 'request_id';
      const { data: ins, error: insErr } = await supabase
        .from(target_table)
        .insert(payload)
        .select(idCol)
        .single();
      if (insErr || !ins) {
        return { ok: false, error: insErr?.message ?? 'insert failed' };
      }
      const target_row_id = (ins as Record<string, string>)[idCol];

      // Compute a tiny diff summary if edited
      let user_edit_summary: string | null = null;
      if (was_edited && originalInput) {
        const changed: string[] = [];
        for (const k of Object.keys(edited_input)) {
          const a = JSON.stringify(originalInput[k] ?? null);
          const b = JSON.stringify(edited_input[k] ?? null);
          if (a !== b) changed.push(k);
        }
        if (changed.length > 0) {
          user_edit_summary = `changed: ${changed.join(', ')}`;
        }
      }

      // Update copilot_actions row
      const { error: updErr } = await supabase
        .from('copilot_actions')
        .update({
          status: was_edited ? 'Edited_And_Committed' : 'Committed',
          committed_payload: payload,
          target_table,
          target_row_id,
          committed_at: new Date().toISOString(),
          user_edit_summary,
        })
        .eq('action_id', action_id);
      if (updErr) {
        console.warn('[use-copilot] failed to update copilot_actions', updErr);
        // we still consider the commit successful — the audit row is best-effort
      }

      setProposalStatus(
        action_id,
        was_edited ? 'Edited_And_Committed' : 'Committed',
        target_row_id,
      );
      return { ok: true, target_row_id };
    },
    [employee?.employee_id, messages, setProposalStatus],
  );

  const discardProposal = useCallback(
    async (action_id: string) => {
      setProposalStatus(action_id, 'Discarded');
      const { error: updErr } = await supabase
        .from('copilot_actions')
        .update({
          status: 'Discarded',
          discarded_at: new Date().toISOString(),
        })
        .eq('action_id', action_id);
      if (updErr) {
        console.warn('[use-copilot] failed to mark discarded', updErr);
      }
    },
    [setProposalStatus],
  );

  return {
    sessionId,
    conversationId,
    messages,
    isSending,
    error,
    failedText,
    streamingText,
    sendMessage,
    loadConversation,
    hasResumableConversation,
    resumeLast,
    commitProposal,
    discardProposal,
    setProposalStatus,
    startNewChat,
  };
}
